import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { patientMedicationInputSchema, type PatientMedicationInput } from "@dosa/validation";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  EmptyState,
  FieldError,
  FieldLabel,
  Input,
  PageLoading,
} from "@dosa/ui";
import { useAuth } from "../../hooks/useAuth";
import { addPatientMedication, listPatientMedications } from "../../lib/api-client";

const statusVariant = { ACTIVE: "success", DISCONTINUED: "neutral", PAUSED: "warning", COMPLETED: "neutral" } as const;

export function PatientMedicationsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const patientId = user!.patientId!;
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data: medications, isLoading } = useQuery({
    queryKey: ["patient-medications", patientId],
    queryFn: () => listPatientMedications(patientId),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PatientMedicationInput>({
    resolver: zodResolver(patientMedicationInputSchema),
    defaultValues: { category: "PRESCRIPTION" },
  });

  const mutation = useMutation({
    mutationFn: (input: PatientMedicationInput) => addPatientMedication(patientId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-medications", patientId] });
      reset();
      setOpen(false);
    },
  });

  if (isLoading) return <PageLoading />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-charcoal-900">{t("patient.medications.title")}</h1>
          <p className="mt-1 text-charcoal-500">{t("patient.medications.subtitle")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger render={<Button />}>
            <Plus className="h-4 w-4" /> {t("patient.medications.addMedication")}
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("patient.medications.addMedication")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4" noValidate>
              <div>
                <FieldLabel htmlFor="freeTextName">{t("common.notes")}</FieldLabel>
                <Input id="freeTextName" placeholder="e.g. Metformin" {...register("freeTextName")} />
                <FieldError>{errors.freeTextName?.message}</FieldError>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <FieldLabel htmlFor="dose">{t("patient.medications.dose")}</FieldLabel>
                  <Input id="dose" placeholder="500 mg" {...register("dose")} />
                </div>
                <div>
                  <FieldLabel htmlFor="prescribedBy">{t("common.notes")}</FieldLabel>
                  <Input id="prescribedBy" placeholder="Dr. Ahmed" {...register("prescribedBy")} />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  {t("common.cancel")}
                </Button>
                <Button type="submit" isLoading={isSubmitting}>
                  {t("common.save")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          {!medications || medications.length === 0 ? (
            <EmptyState className="border-0" title={t("patient.medications.empty")} />
          ) : (
            <ul className="divide-y divide-charcoal-100">
              {medications.map((m) => (
                <li key={m.id} className="flex items-center justify-between px-5 py-4">
                  <div>
                    <p className="font-medium text-charcoal-800">{m.freeTextName}</p>
                    <p className="text-sm text-charcoal-400">
                      {m.dose} {m.frequency ? `· ${t(`patient.medications.${m.category.toLowerCase()}`)}` : ""}
                    </p>
                  </div>
                  <Badge variant={statusVariant[m.status]}>{t(`patient.medications.${m.status.toLowerCase()}`)}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
